import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';

import { useCurrentProject } from '@adsk/alloy-provider';
import Modal from '@adsk/alloy-react-modal';

import EntityPickerModalBody from './EntityPickerModalBody';
import EntityPickerModalFooter, {
  EntityPickerModalFooterProps,
} from './EntityPickerModalFooter';
import { isPluginProductOrToolNotAvailableInEntitlements } from '../helpers/isPluginProductOrToolNotAvailableInEntitlements';
import useCanCreate from '../hooks/useCanCreate';
import useEntitySelection from '../hooks/useEntitySelection';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';
import {
  AddEntitiesToCacheProps,
  EntityWithId,
  CreateFormProps,
  UpdateEntitiesInCache,
} from '../types/types';

export interface ConnectedEntityPickerModalBaseProps<T = EntityWithId> {
  open?: boolean;
  sourceEntityType?: string;
  plugin?: CommonReferencePickerPlugin | null;
  closeModal?: () => void;
  container?: unknown;
  markAsSelectedIds?: string[];
  disabledIds?: string[];
  onCreateSuccess?: (
    entities: T | T[],
    option?: { selectedEntities: T[] },
  ) => void;
  isSaving?: boolean;
  loading?: boolean;
  error?: boolean;
  retry?: () => void;
  allowCreate?: boolean;
  createFormProps?: CreateFormProps;
  dataViewProps?: Record<string, unknown>;
  addEntitiesToCache?: (option: AddEntitiesToCacheProps) => void;
  onUpdate?: (entity: T) => void;
  saveButtonText?: ReactNode;
  title?: ReactNode;
  infoMessage?: ReactNode;
  renderFooter?: (props: EntityPickerModalFooterProps<T>) => ReactNode;
  selectedEntities?: T[];
  setSelectedEntities?: (selectedEntities: T[]) => void;
  showTabTitle?: boolean;
  updateEntitiesInCache?: UpdateEntitiesInCache;
}

interface MultipleTrueProps<T> extends ConnectedEntityPickerModalBaseProps<T> {
  multiple: true;
  onSave: (selectedEntities: T[]) => Promise<void> | void;
}

interface MultipleFalseProps<T> extends ConnectedEntityPickerModalBaseProps<T> {
  multiple?: false | undefined;
  onSave: (selectedEntity: T) => Promise<void> | void;
}

export type ConnectedEntityPickerModalProps<T = EntityWithId> =
  | MultipleTrueProps<T>
  | MultipleFalseProps<T>;

const ConnectedEntityPickerModal = <T extends EntityWithId>({
  open,
  sourceEntityType,
  plugin,
  closeModal,
  container,
  markAsSelectedIds = [],
  disabledIds = [],
  onSave,
  onCreateSuccess,
  isSaving,
  loading,
  error,
  retry,
  allowCreate,
  multiple,
  createFormProps,
  dataViewProps,
  addEntitiesToCache,
  onUpdate,
  saveButtonText,
  title,
  infoMessage,
  renderFooter,
  selectedEntities: selectedEntitiesProp,
  setSelectedEntities: setSelectedEntitiesProp,
  showTabTitle = true,
}: ConnectedEntityPickerModalProps<T>) => {
  const CustomTopBanner = plugin?.CustomTopBanner;
  const currentProject = useCurrentProject();
  const { selectedEntities, onSelectionChange, setSelection, clearSelection } =
    useEntitySelection<T>({
      multiple: !!multiple,
      selectedEntities: selectedEntitiesProp,
      setSelectedEntities: setSelectedEntitiesProp,
    });

  const onCreateSuccessHandler = useCallback(
    (entities: T | T[]) => onCreateSuccess?.(entities, { selectedEntities }),
    [onCreateSuccess, selectedEntities],
  );

  const isPluginDisabledInEntitlements = useMemo(
    () =>
      isPluginProductOrToolNotAvailableInEntitlements({
        plugin,
        currentProject,
      }),
    [plugin, currentProject],
  );

  const [createPanelOpen, setCreatePanelOpen] = useState(false);

  const manuallySelectedIds = useMemo(
    () => selectedEntities.map((e) => e.id),
    [selectedEntities],
  );
  const selectedIds: string[] = useMemo(
    () => [...new Set([...manuallySelectedIds, ...(markAsSelectedIds ?? [])])],
    [manuallySelectedIds, markAsSelectedIds],
  );

  useEffect(() => {
    if (!open) {
      clearSelection();
      setCreatePanelOpen(false);
    }
    // we only want this called when open changes
    // we cannot rely on clearSelection not changing when this component is controlled
    // because we cannot assume that the setSelectedEntities prop will be memoized
    // also we don't care if clearSelection changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const additionalArgs = useMemo(() => {
    if (plugin && plugin.getCanCreateArgs) {
      return plugin.getCanCreateArgs({ createFormProps });
    }

    return null;
  }, [plugin, createFormProps]);

  const userPermissionsData = useCanCreate(
    plugin,
    !allowCreate,
    additionalArgs,
  );

  const renderFooterComponent =
    renderFooter ||
    ((props: EntityPickerModalFooterProps<T>) => (
      <EntityPickerModalFooter {...props} />
    ));

  const PluginModalTitle = plugin?.PickerModalTitle;
  const modalTitle =
    title ||
    (plugin && PluginModalTitle && (
      <PluginModalTitle showTabTitle={showTabTitle} />
    ));

  const renderFooterProps = {
    selectedEntities,
    plugin,
    clearSelection,
    isSaving,
    createPanelOpen,
    closeModal,
    saveButtonText,
  };

  return (
    <Modal
      open={open}
      container={container}
      size={Modal.SIZES.EXTRA_LARGE}
      {...plugin?.modalProps}
      // Prevents datadog from logging any PII contained within this modal
      // Added here a modal cannot be wrapped by consumers
      data-dd-action-name="<MASKED-DATA>"
    >
      <Modal.Header>
        {modalTitle}
        <Modal.Close onClick={closeModal} />
      </Modal.Header>
      <Modal.Body
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        }}
      >
        {CustomTopBanner && <CustomTopBanner multiple={!!multiple} />}
        <EntityPickerModalBody<T>
          sourceEntityType={sourceEntityType}
          plugin={plugin}
          userPermissionsData={userPermissionsData}
          loading={loading}
          error={error}
          retry={retry}
          setSelection={setSelection}
          onChange={onSelectionChange}
          isSaving={isSaving}
          selectedIds={selectedIds}
          selectedEntities={selectedEntities}
          disabledIds={disabledIds}
          multiple={multiple}
          addEntitiesToCache={addEntitiesToCache}
          onUpdate={onUpdate}
          onCreateSuccess={onCreateSuccessHandler}
          createFormProps={createFormProps}
          dataViewProps={dataViewProps}
          createPanelOpen={createPanelOpen}
          setCreatePanelOpen={setCreatePanelOpen}
          infoMessage={infoMessage}
          isPluginDisabledInEntitlements={isPluginDisabledInEntitlements}
        />
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: 'space-between' }}>
        {multiple
          ? renderFooterComponent({
              multiple: true,
              onSave,
              ...renderFooterProps,
            })
          : renderFooterComponent({
              multiple: false,
              onSave,
              ...renderFooterProps,
            })}
      </Modal.Footer>
    </Modal>
  );
};

export default ConnectedEntityPickerModal;
